import {
  Copy,
  ListOrdered,
  Move,
  QrCode,
  Smartphone,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18nContext } from '@/i18n/i18n-react';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import {
  moreVM,
  type MoreTileId,
} from '@/components/whats-new/editions/estreia/view-models';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { GlassCard } from '@/components/whats-new/slides/glass-card';
import { Rise } from '@/components/whats-new/slides/rise';
import {
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

const ICONS: Record<MoreTileId, LucideIcon> = {
  team: Users,
  testPush: QrCode,
  duplicate: Copy,
  orders: ListOrdered,
  audiences: Move,
  signup: Smartphone,
};

/**
 * 8 · E mais (NEWS-35): six smaller news as tiles (Equipe, Teste no seu
 * celular, Duplicar campanha, Pedidos no Analytics, Públicos arrastando,
 * Cadastro pelo celular), each with where to find it ("Duplicar" names the
 * top campaign's copy). 3 columns from 768px, 2 below without the
 * descriptions.
 */
export function MoreSlide(_props: SlideProps) {
  const { LL } = useI18nContext();
  const { tiles } = moreVM(useEstreiaFacts());

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.more.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.more.title()} />
          </SlideTitle>
          <Rise index={2}>
            <SlideLead>{LL.whatsNew.editions.estreia.more.lead()}</SlideLead>
          </Rise>
        </>
      }
      visual={
        <div
          data-testid="more-tiles"
          className="grid grid-cols-3 gap-3 max-md:grid-cols-2 max-md:gap-2.5"
        >
          {tiles.map((tile, i) => {
            const Icon = ICONS[tile.id];
            return (
              <Rise key={tile.id} index={i + 1} className="min-w-0">
                <GlassCard className="flex h-full flex-col gap-2.5 p-4 max-md:p-3.5">
                  <span className="grid size-9 place-items-center rounded-[10px] bg-[rgba(111,160,255,0.18)] text-[#CFE0FF]">
                    <Icon
                      aria-hidden="true"
                      className="size-[18px]"
                      strokeWidth={1.6}
                    />
                  </span>
                  <b
                    data-testid="more-tile-title"
                    className="text-[15px] font-semibold tracking-[-0.01em] max-md:text-sm"
                  >
                    {tile.title(LL)}
                  </b>
                  <p
                    data-testid="more-tile-description"
                    className="m-0 text-[12.5px] leading-[1.45] text-white/[.86] max-md:hidden"
                  >
                    {tile.description(LL)}
                  </p>
                  {tile.where.state === 'ready' ? (
                    <span
                      data-testid="more-tile-where"
                      className="mt-auto text-xs font-semibold tabular-nums text-[#CFE0FF]"
                    >
                      {tile.where.value(LL)}
                    </span>
                  ) : (
                    <Skeleton
                      data-testid="more-tile-loading"
                      className="mt-auto h-3.5 w-24 bg-white/[.12]"
                    />
                  )}
                </GlassCard>
              </Rise>
            );
          })}
        </div>
      }
    />
  );
}
